import React, { useState, useEffect } from 'react';
import { Stack, Divider } from '@mui/material';
import { FieldOption } from 'views/App/Portal/PerformanceReporting/FieldBank/_types';
import { TemplateField, SectionFieldOption } from '../../_types';
import { DirectFieldInput, BaseSelectInput } from 'views/App/Portal/PerformanceReporting/_components/FieldInputs';
import SingleSelectOptionManager from 'views/App/Portal/PerformanceReporting/_components/SingleSelectOptionManager';
import dayjs, { Dayjs } from 'dayjs';
import { useTemplateBuilderContext } from '../context';
import useTranslations from 'hooks/useTranslations';

interface DefaultValuePickerProps {
  field: TemplateField;
  value: string[];
  onChange: (value: string[]) => void;
  onOptionsChange?: (options: SectionFieldOption[]) => void;
  trackUserActivity?: () => void;
  disabled?: boolean;
}

const DefaultValuePicker: React.FC<DefaultValuePickerProps> = ({
  field,
  value,
  onChange,
  onOptionsChange,
  trackUserActivity,
  disabled = false,
}) => {
  const t = useTranslations();

  // Get validation errors from context
  const { fieldOptionValidationErrors } = useTemplateBuilderContext();

  // Use fieldType from template field (backend will provide correct fieldType in sectionFields)
  const fieldType = field.fieldType || 'Text';

  // Helper function to check if an option is valid for default value selection
  const isValidOption = (opt: SectionFieldOption): boolean => {
    if (opt.status !== 'Active') return false;
    if (!opt.effectiveLabel || opt.effectiveLabel.trim() === '') return false;
    if (opt.effectiveIsDeleted) return false;
    return true;
  };

  // Initialize field options from the field data
  const [fieldOptions, setFieldOptions] = useState<SectionFieldOption[]>(() => {
    // Use section field options from template field
    const existingOptions = field.sectionFieldOptions || [];

    if (existingOptions.length > 0) {
      return existingOptions.map((opt: SectionFieldOption) => ({
        ...opt,
        // Ensure status is set - default to Active if not specified
        status: opt.status || ('Active' as 'Active' | 'Inactive'),
      }));
    }

    // For SingleSelect/MultiSelect fields with no existing options, create 2 empty ones
    if (fieldType === 'SingleSelect' || fieldType === 'MultiSelect') {
      return [
        {
          label: '',
          effectiveLabel: '',
          status: 'Active' as 'Active' | 'Inactive',
          isDeleted: false,
          effectiveIsDeleted: false,
          parentSectionFieldOptionId: null,
        },
        {
          label: '',
          effectiveLabel: '',
          status: 'Active' as 'Active' | 'Inactive',
          isDeleted: false,
          effectiveIsDeleted: false,
          parentSectionFieldOptionId: null,
        },
      ];
    }

    // For other field types, return empty array
    return [];
  });

  // Update local state when field changes
  useEffect(() => {
    const existingOptions = field.sectionFieldOptions || [];

    if (existingOptions.length > 0) {
      setFieldOptions(
        existingOptions.map((opt: SectionFieldOption) => ({
          ...opt,
          status: opt.status || ('Active' as 'Active' | 'Inactive'),
        })),
      );
    } else {
      // For SingleSelect/MultiSelect fields with no existing options, create 2 empty ones
      if (fieldType === 'SingleSelect' || fieldType === 'MultiSelect') {
        setFieldOptions([
          {
            label: '',
            effectiveLabel: '',
            status: 'Active' as 'Active' | 'Inactive',
            isDeleted: false,
            effectiveIsDeleted: false,
            parentSectionFieldOptionId: null,
          },
          {
            label: '',
            effectiveLabel: '',
            status: 'Active' as 'Active' | 'Inactive',
            isDeleted: false,
            effectiveIsDeleted: false,
            parentSectionFieldOptionId: null,
          },
        ]);
      } else {
        // For other field types, clear options
        setFieldOptions([]);
      }
    }
  }, [field.sectionFieldOptions, field.id, fieldType]);

  // Check if current default value is still valid when field options change
  useEffect(() => {
    if ((fieldType === 'SingleSelect' || fieldType === 'MultiSelect') && value.length > 0) {
      // Don't clear inherited default values - the effective value comes from the parent
      const isInheritedDefault = field.parentSectionFieldId !== null && field.defaultValues === null;
      if (isInheritedDefault) return;

      const currentOptions = field.sectionFieldOptions || [];
      const validLabels = currentOptions
        .filter((opt) => {
          if (opt.status !== 'Active') return false;
          if (!opt.effectiveLabel || opt.effectiveLabel.trim() === '') return false;
          if (opt.isDeleted || opt.effectiveIsDeleted) return false;
          return true;
        })
        .map((opt) => opt.effectiveLabel);

      if (fieldType === 'SingleSelect') {
        if (!validLabels.includes(value[0])) {
          onChange([]);
        }
      } else {
        const filtered = value.filter((v) => validLabels.includes(v));
        if (filtered.length !== value.length) {
          onChange(filtered);
        }
      }
    }
  }, [field.sectionFieldOptions, value, fieldType, onChange, field.parentSectionFieldId, field.defaultValues]);

  // Common text field styles
  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'background.paper',
      fontSize: '0.875rem',
    },
  };

  // Handle value conversion for Date/Time fields (value is string[])
  const getFieldValue = () => {
    const first = value[0];
    if (fieldType === 'Date' || fieldType === 'Time') {
      if (first && first !== '') {
        return dayjs(first);
      }
      return null;
    }
    return first ?? '';
  };

  const handleFieldChange = (newValue: any) => {
    if (trackUserActivity && (fieldType === 'Text' || fieldType === 'Number' || fieldType === 'Lookup')) {
      trackUserActivity();
    }

    if (fieldType === 'Date' || fieldType === 'Time') {
      if (newValue && dayjs.isDayjs(newValue)) {
        if (fieldType === 'Date') {
          onChange([newValue.format('YYYY-MM-DD')]);
        } else if (fieldType === 'Time') {
          onChange([newValue.toISOString()]);
        }
      } else if (typeof newValue === 'string' && newValue.trim() !== '') {
        onChange([newValue]);
      } else {
        onChange([]);
      }
    } else {
      onChange(newValue != null && newValue !== '' ? [String(newValue)] : []);
    }
  };

  const renderSingleSelectInput = (isMulti: boolean) => {
    // Get visible options for default value dropdown
    const visibleOptions = fieldOptions.filter(isValidOption);

    // Convert SectionFieldOption[] to FieldOption[] for SingleSelectOptionManager
    const fieldOptionsForManager: FieldOption[] = fieldOptions.map((opt) => ({
      ...opt,
      label: opt.effectiveLabel || '', // Use effectiveLabel as label (never null)
      isVisible: opt.isDeleted === null ? true : !opt.isDeleted,
    }));

    const handleOptionsChange = (newOptions: FieldOption[]) => {
      // Convert FieldOption to SectionFieldOption
      // Preserve parentSectionFieldOptionId from original options to maintain inheritance relationships
      const sectionFieldOptions: SectionFieldOption[] = newOptions.map((opt) => {
        // Find the original option to preserve parentSectionFieldOptionId and other properties
        const originalOption = fieldOptions.find(
          (original) =>
            // Match by id if both have non-zero ids (saved options)
            (original.id && opt.id && original.id === opt.id && original.id !== 0) ||
            // Match by fieldOptionId if both have it (inherited options from field bank)
            (original.fieldOptionId && opt.fieldOptionId && original.fieldOptionId === opt.fieldOptionId),
        );

        return {
          ...opt,
          id: opt.id ?? 0, // Ensure new options have id: 0, not null (backend requires Int32)
          fieldOptionId: opt.fieldOptionId ?? null, // Ensure consistency
          label: opt.label,
          effectiveLabel: opt.label,
          // Convert isVisible to isDeleted (inverted logic)
          // User edits are explicit, so convert to false (not deleted) or true (deleted), not null
          isDeleted: opt.isVisible === false, // false = deleted, true/undefined = not deleted
          effectiveIsDeleted: opt.isVisible === false,
          // Preserve parentSectionFieldOptionId from original option to maintain inheritance
          // Only set to null for truly new options (no matching original)
          parentSectionFieldOptionId: originalOption?.parentSectionFieldOptionId ?? null,
          // Preserve rank from original if it exists, otherwise use the index
          rank: originalOption?.rank ?? opt.rank ?? 0,
        };
      });

      setFieldOptions(sectionFieldOptions);
      onOptionsChange?.(sectionFieldOptions);

      // If the current default value is no longer valid, clear or filter it
      const isInheritedDefault = field.parentSectionFieldId !== null && field.defaultValues === null;
      if (!isInheritedDefault && value.length > 0) {
        const validLabels = sectionFieldOptions.filter(isValidOption).map((opt) => opt.effectiveLabel);
        if (fieldType === 'SingleSelect') {
          if (!validLabels.includes(value[0])) {
            onChange([]);
          }
        } else {
          const filtered = value.filter((v) => validLabels.includes(v));
          if (filtered.length !== value.length) {
            onChange(filtered);
          }
        }
      }
    };

    return (
      <Stack spacing={2}>
        {/* Options Management using shared component - only show if not disabled */}
        {!disabled && (
          <>
            <SingleSelectOptionManager
              options={fieldOptionsForManager}
              onChange={handleOptionsChange}
              mode='templateBuilder'
              trackUserActivity={trackUserActivity}
              validationError={fieldOptionValidationErrors[field.id] || ''}
            />

            {/* Divider */}
            <Divider />
          </>
        )}

        {/* Default Value Selection */}
        <BaseSelectInput
          options={visibleOptions.map((opt) => ({
            value: opt.effectiveLabel,
            label: opt.effectiveLabel,
          }))}
          value={isMulti ? value : (value[0] ?? '')}
          onChange={(newValue) => {
            if (isMulti && Array.isArray(newValue)) {
              onChange(newValue);
            } else if (!isMulti && typeof newValue === 'string') {
              onChange(newValue ? [newValue] : []);
            } else if (!isMulti) {
              onChange([]);
            }
          }}
          isMulti={isMulti}
          label={t('performanceReporting.templates.builder.fieldProperties.defaultValue.label')}
          placeholder={t('performanceReporting.templates.builder.fieldProperties.defaultValue.chooseOption')}
          disabled={disabled}
          size='small'
          fullWidth={true}
          isClearable={true}
          helperText={
            visibleOptions.length === 0
              ? t('performanceReporting.templates.builder.fieldProperties.defaultValue.noOptionsHelper')
              : undefined
          }
        />
      </Stack>
    );
  };

  if (fieldType === 'SingleSelect') {
    return renderSingleSelectInput(false);
  }
  if (fieldType === 'MultiSelect') {
    return renderSingleSelectInput(true);
  }

  // For all other field types, use the shared DirectFieldInput component
  return (
    <DirectFieldInput
      fieldType={fieldType}
      value={getFieldValue()}
      onChange={handleFieldChange}
      size='small'
      fullWidth={true}
      hideLabel={true}
      disabled={disabled}
    />
  );
};

export default DefaultValuePicker;
